import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../supabase";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import EmailVerification from "./EmailVerification";
import pwsLogo from "../../../assets/logo800.webp";
import ImageWithSkeleton from "../../../components/ui/ImageWithSkeleton";
import packageJson from "../../../../package.json";
import "./LogIn.css";

export default function Login() {
  const { t } = useTranslation('login');
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [registrationMessage, setRegistrationMessage] = useState("");

  useEffect(() => {
    // Check for registration message from CreateUser
    if (location.state?.message) {
      setRegistrationMessage(location.state.message);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only auto-redirect if user is authenticated AND we're not in OTP mode AND not loading
      if (session?.user && session.user.email_confirmed_at && !otpSent && !loading) {
        navigate("/profile");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location, otpSent, loading]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    const normalizedEmail = email.trim().toLowerCase();

    // Step 1: If OTP not sent yet, verify credentials and send OTP
    if (!otpSent) {
      try {
        const { error } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });

        if (error) {
          if (error.code === "email_not_confirmed") {
            setNeedsVerification(true);
            setErrorMsg(t('mustVerifyEmail'));
          } else {
            // Supabase deliberately doesn't distinguish "no such user" from
            // "wrong password" (anti-enumeration) - one generic message covers both.
            setErrorMsg(t('invalidCredentials'));
          }
          setLoading(false);
          return;
        }

        // Email/password verified - ask the server whether this account
        // already did 2FA this calendar month (server-side check, not a
        // client flag someone could forge via devtools to skip OTP entirely).
        try {
          const otpResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/send-otp`, {
            email: normalizedEmail
          });

          if (otpResponse.data?.skipOtp) {
            localStorage.setItem('pws_auth', '1');
            setLoading(false);
            navigate("/profile");
            return;
          }

          // Sign out temporarily until OTP is verified
          await supabase.auth.signOut();

          // Show OTP input field
          setOtpSent(true);
          setResendTimer(60);
          setErrorMsg("");
          setLoading(false);
        } catch (otpError) {
          console.error("OTP send error:", otpError);
          setErrorMsg(t('couldNotSendCode'));
          await supabase.auth.signOut();
          setLoading(false);
        }
      } catch (error) {
        setErrorMsg(t('loginErrorOccurred'));
        setLoading(false);
      }
    }
    // Step 2: OTP sent, now verify OTP
    else {
      if (otp.length !== 6) {
        setErrorMsg(t('enter6DigitCode'));
        setLoading(false);
        return;
      }

      try {
        // Verify OTP with backend
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/verify-otp`, {
          email: normalizedEmail,
          otp: otp.replace(/\D/g, '').trim()
        });

        if (response.data.success) {
          // OTP verified, now sign in for real
          try {
            await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
            localStorage.setItem('pws_auth', '1');

            // Wait a moment for the session to fully settle before navigating
            setTimeout(() => {
              navigate("/profile");
            }, 100);
          } catch (authError) {
            console.error("Final auth error:", authError);
            setErrorMsg(t('couldNotLogIn'));
            setLoading(false);
          }
        } else {
          setErrorMsg(response.data.message || t('invalidCode'));
          setLoading(false);
        }
      } catch (error) {
        const status = error?.response?.status;
        const backendMessage = error?.response?.data?.message;
        console.error("OTP verify failed", {
          status,
          data: error?.response?.data,
          message: error?.message,
        });
        if (status === 400) {
          setErrorMsg(backendMessage || t('invalidOrExpiredCode'));
        } else {
          console.error("OTP verification error:", error);
          setErrorMsg(backendMessage || t('somethingWentWrongVerifying'));
        }
        setLoading(false);
      }
    }
  }

  async function handleResendOTP() {
    setLoading(true);
    setErrorMsg("");

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/send-otp`, {
        email: email.trim().toLowerCase()
      });
      setResendTimer(60);
      setOtp("");
      setLoading(false);
    } catch (error) {
      console.error("Resend OTP error:", error);
      setErrorMsg(t('couldNotSendNewCode'));
      setLoading(false);
    }
  }

  const handleVerificationComplete = () => {
    setNeedsVerification(false);
    navigate("/profile");
  };

  // Show email verification component if user is not verified
  if (needsVerification) {
    return (
      <>
        <EmailVerification
          email={email.trim().toLowerCase()}
          password={password}
          onVerified={handleVerificationComplete}
        />
      </>
    );
  }

  return (
    <div className="login-wrapper">
      <div className="login-content">
        <div className="login-card">
          <form className="login-form-new" onSubmit={handleLogin}>
            <div className="login-form-header">
              <div className="login-logo-wrap">
                <ImageWithSkeleton
                  src={pwsLogo}
                  alt="PLAYWORLD"
                  loading="eager"
                  imgStyle={{ width: "100%", height: "auto", display: "block" }}
                  style={{ width: "100%", zIndex: 2, position: "relative" }}
                />
              </div>
              <div className="login-card-title">{t('welcomeBack')}</div>
            </div>

            {registrationMessage && (
              <div className="login-message-success">
                {registrationMessage}
              </div>
            )}

            {!otpSent && (
              <>
                <div className="login-field">
                  <label>{t('email')}</label>
                  <input
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="login-field">
                  <label>{t('password')}</label>
                  <div className="login-password-wrap">
                    <input
                      id="passwordInput"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <span
                      className="login-password-toggle"
                      onClick={() => {
                        const input = document.getElementById('passwordInput');
                        if (input.type === 'password') {
                          input.type = 'text';
                        } else {
                          input.type = 'password';
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </span>
                  </div>
                </div>

                <button className="login-btn-submit" type="submit" disabled={loading}>
                  {loading ? <span className="login-btn-dots"><span></span><span></span><span></span></span> : t('logIn')}
                </button>

                <div className="login-forgot-row">
                  <button
                    type="button"
                    className="login-forgot-link"
                    onClick={() => navigate('/forgot-password')}
                  >
                    {t('forgotPassword')}
                  </button>
                </div>
              </>
            )}

            {otpSent && (
              <>
                <div className="login-message-info">
                  {t('codeSentToPrefix')} <strong>{email}</strong>
                </div>

                <div className="login-field">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    required
                    autoFocus
                    className="login-otp-input"
                  />
                </div>

                <button
                  className="login-btn-submit"
                  type="submit"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? <span className="login-btn-dots"><span></span><span></span><span></span></span> : t('verify')}
                </button>

                <button
                  type="button"
                  className="login-resend-btn"
                  onClick={handleResendOTP}
                  disabled={resendTimer > 0 || loading}
                >
                  {resendTimer > 0 ? t('resendCodeIn', { seconds: resendTimer }) : t('resendCode')}
                </button>
              </>
            )}

            {errorMsg && <div className="login-error-message">{errorMsg}</div>}

            <div className="login-divider-row">
              <div className="login-divider-line"></div>
            </div>

            <div className="login-signup-row">
              {t('noAccount')}{' '}
              <button
                type="button"
                className="login-signup-link"
                onClick={() => navigate("/CreateUser")}
              >
                {t('createAccountHere')}
              </button>
            </div>
          </form>
          <p className="profile-app-version">v{packageJson.version}</p>
        </div>
      </div>
    </div>
  );
}
